// What an open guarantees and what it delivered, in the contract's own arithmetic
// (builders-onchain.md §1): fees are bps of the gross, the net is what solvers quote on, the
// expected amount is the net at the signed rate, the floor is that less the slippage. Every step
// floors, as on chain, so the demo shows exactly what the open will carry.

import type { IntentQuote, IntentRow, Quote, QuoteHead } from "./api";

const BPS = 10_000n;

export function openTerms(gross: bigint, builderFeeBps: number, protocolFeeBps: number, rateNum: bigint, rateDen: bigint, slippageBps: number) {
  const net = gross - (gross * BigInt(builderFeeBps)) / BPS - (gross * BigInt(protocolFeeBps)) / BPS;
  const expected = rateDen === 0n ? 0n : (net * rateNum) / rateDen;
  const min = (expected * (BPS - BigInt(slippageBps))) / BPS;
  return { net, expected, min };
}

/** What the recipient is guaranteed for `q` at the user's `slippageBps` (the api's
 *  `min_dst_at_min_gross` is the same at the solver's): the least floor any gross in the band can
 *  give, as `gi_core::terms::guaranteed_floor` computes it. A plain open's band is the quoted
 *  gross alone, so this is exactly its floor. A hosted swap's open carries whatever the swap
 *  delivers inside the band, and the two fees round down independently, so the net is bounded
 *  below by ⌈min_gross × (10 000 − fees) / 10 000⌉. */
export function guaranteed(head: QuoteHead, q: Quote, slippageBps: number): bigint {
  const lo = BigInt(head.min_gross ?? head.gross);
  const hi = BigInt(head.max_gross ?? head.gross);
  const [num, den] = [BigInt(q.dst_amount), BigInt(head.net)];
  if (lo === hi) return openTerms(lo, head.builder_fee_bps, head.protocol_fee_bps, num, den, slippageBps).min;
  const keep = BPS - BigInt(head.builder_fee_bps) - BigInt(head.protocol_fee_bps);
  const netLb = (lo * keep + BPS - 1n) / BPS;
  const expected = den === 0n ? 0n : (netLb * num) / den;
  return (expected * (BPS - BigInt(slippageBps))) / BPS;
}

/** Whether a fresh quote may replace the one the user confirmed without asking again: only if it
 *  guarantees at least as much. */
export const notWorse = (confirmed: bigint, fresh: bigint) => fresh >= confirmed;

/** `(a − b) / b` in bps, rounded toward zero; negative when `a` is less. */
export const diffBps = (a: bigint, b: bigint): number | undefined => (b === 0n ? undefined : Number(((a - b) * BPS) / b));

export type Receipt = {
  /** what the solver quoted (the expected amount the user was shown) */
  quoted?: bigint;
  /** the floor the open carried on chain */
  floor: bigint;
  /** what was delivered */
  received?: bigint;
  /** received against the quote */
  vsQuoteBps?: number;
  /** hosted swap: the expected amount at the net that actually arrived (the rate scales with the
   *  swap's real output, so a difference from the quote there is the swap's, not the solver's) */
  expectedAtActual?: bigint;
  vsExpectedBps?: number;
};

export function receipt(row: IntentRow, quote: IntentQuote | null | undefined): Receipt {
  const floor = BigInt(row.min_dst_amount);
  const received = row.fulfill_amount != null ? BigInt(row.fulfill_amount) : undefined;
  const quotedRaw = quote?.dst_amount ?? row.quoted_dst_amount ?? undefined;
  const quoted = quotedRaw != null ? BigInt(quotedRaw) : undefined;
  const expectedAtActual = quote?.source_swap && quote.expected_at_actual != null ? BigInt(quote.expected_at_actual) : undefined;
  return {
    quoted,
    floor,
    received,
    vsQuoteBps: received !== undefined && quoted !== undefined ? diffBps(received, quoted) : undefined,
    expectedAtActual,
    vsExpectedBps: received !== undefined && expectedAtActual !== undefined ? diffBps(received, expectedAtActual) : undefined,
  };
}

/** `-49` → `−0.49% (−49 bps)`. */
export function fmtBps(bps: number): string {
  const sign = bps > 0 ? "+" : bps < 0 ? "−" : "";
  const a = Math.abs(bps);
  return `${sign}${(a / 100).toFixed(2)}% (${sign}${a} bps)`;
}

/** The receipt's "paid" line. Linked to its build, it is what the user paid: the token they started
 *  with for a hosted swap, else the stable's gross. Unlinked (the build record is best effort), the
 *  source payment is unknown: a hosted swap's escrow holds the swap's output, not what was paid, so
 *  the line is only what went into escrow. */
export function paidLine(row: IntentRow, quote: IntentQuote | null | undefined): { label: string; amount: string; token: string } {
  if (quote?.source_swap) return { label: "You paid", amount: quote.source_swap.input_amount, token: quote.source_swap.input_token };
  const gross = (BigInt(row.input_amount) + BigInt(row.builder_fee ?? "0") + BigInt(row.protocol_fee ?? "0")).toString();
  return quote ? { label: "You paid", amount: gross, token: "stable" } : { label: "Put in escrow", amount: gross, token: "stable" };
}

